// IOL Exchange — presentation helpers.
//
// The calculation itself is NOT here. [F1]–[F6] run in the backend engine
// (toricaligner.iolExchange.calculator), which is validated against Dr. Lombard's
// workbook by a frozen fixture suite. Keeping a second implementation in the
// browser would mean two things to keep correct, and the browser copy would not
// be covered by those fixtures.
//
// Call the engine through services/IOLExchange.js.
//
// What stays in this file is only what the form needs before Calculate is
// pressed, plus row labelling.

import { exchangeIOLModelNames } from '../helpers/enums/IOLExchangeModels'
import { usesToric } from '../services/IOLExchange'

// Approximate validated power range (SRS 1.7 "Extrapolation"), and which answers to
// "Use toric for exchange?" run the toric block.
// Re-exported from the service so there is a single definition.
export { MIN_SAFE_POWER, MAX_SAFE_POWER, usesToric } from '../services/IOLExchange'

// Inputs the workbook refuses to calculate ('No-Biometry IOLX'!M1, reasons in
// F15). The engine refuses them too; the form checks first so the surgeon sees
// why instead of a server error.
// Current IOL power: 0.0-40.0 D in 0.5 D steps. That is the workbook's own wide
// range - the ~6-27 D band stays a warning (SRS O-3).
export const CURRENT_POWER_MIN = 0;
export const CURRENT_POWER_MAX = 40;
export const POWER_STEP = 0.5;
// SRS 1.1: sphere and cylinder are "Signed, 2 decimals"; the target is typed
// the same way. The web API refuses more.
export const MAX_DECIMALS = 2;

// IOLX!F15 refuses "Change IOL model? = Yes" with the same model picked twice:
// "Set Change IOL Model? to No, or select a different New IOL model". Only a
// real model counts - both selects start on (different) placeholders.
export function isSameModelChange(changeModel, fromModelList, currentModel, newModel){
    return changeModel && fromModelList
        && exchangeIOLModelNames.includes(currentModel) && currentModel === newModel;
}

// IOLX!F15 warns, and still calculates, when two manual A-constants are
// identical: the model-change A-shift is then 0.00 D.
export function hasIdenticalManualAConstants(changeModel, fromModelList, currentA, newA){
    let a = parseFloat(currentA);
    let b = parseFloat(newA);
    return changeModel && !fromModelList && !isNaN(a) && !isNaN(b) && Math.abs(a - b) < 0.000001;
}

// A refraction in dioptres as the workbook prints it: 2 decimals, "+" on
// positives, format "+0.00;-0.00;0.00" (No-Biometry IOLX!L6:L18, I10, B11).
//
// Rounds half away from zero on the DECIMAL value, as the workbook's TEXT and
// ROUND do. toFixed alone rounds the binary double instead, so an exact tie
// such as -2.425 (stored as -2.42499999...) printed -2.42 where the workbook
// shows -2.43. The engine reports 10 decimals, and toFixed(10) recovers them
// exactly, so the rounding below works on the true decimal.
export function formatDiopters(value){
    if(value === 0) return "0.00";
    let hundredths = Math.round(Number(Math.abs(value).toFixed(10) + "e2"));
    return ((value < 0)? "-" : "+") + (hundredths / 100).toFixed(2);
}

// Toric block (Dr. Buonsanti's workbook, sheet Calculadora). Its tables cover SEQ
// IOL powers 0.0-34.5 D and it does not extrapolate (Calculadora!A22), so a toric
// calculation needs the implanted power inside that range; the server refuses
// anything else. A toric lens is calculated for the bag only.
export const TORIC_POWER_MIN = 0;
export const TORIC_POWER_MAX = 34.5;

// What stops a toric calculation, as text for the form; null when nothing does. The two
// causes are also given apart, so the form can show each next to its own field: the
// toric question sits at the top of the form, away from both.
export function getToricPowerBlocker(useToric, currentPower){
    if(!usesToric(useToric)) return null;
    let power = parseFloat(currentPower);
    if(!isNaN(power) && (power < TORIC_POWER_MIN || power > TORIC_POWER_MAX)){
        return "The toric calculation covers implanted SEQ powers of "
            + TORIC_POWER_MIN.toFixed(1) + "–" + TORIC_POWER_MAX.toFixed(1) + " D.";
    }
    return null;
}

export function getToricPositionBlocker(useToric, newPosition){
    if(usesToric(useToric) && newPosition === 'Sulcus'){
        return "The toric calculation is for a lens placed in the bag.";
    }
    return null;
}

export function getToricBlocker(useToric, currentPower, newPosition){
    return getToricPowerBlocker(useToric, currentPower) || getToricPositionBlocker(useToric, newPosition);
}

// An axis in degrees as the workbook prints it, 1 decimal (Calculadora!F13, G27),
// rounded half away from zero on the decimal value. 0 and 180 are the same
// meridian and 180 is shown (Guia B16). Null - the workbook's "Sin eje" - is "—".
export function formatAxis(value){
    if(value === null || value === undefined) return "—";
    let tenths = Math.round(Number(value.toFixed(10) + "e1"));
    if(tenths === 0) tenths = 1800;
    return (tenths / 10).toFixed(1) + "°";
}

// SRS 1.7: "when sulcus is selected the UI shows a reminder". Mirrors
// 'No-Biometry IOLX'!F18, which shows it whenever sulcus is selected: with the
// bag/sulcus pair when both ladders recommend a power, and the plain reminder
// otherwise - no bag candidate, or no candidate at all.
// `result` is the adapted engine result (services/IOLExchange.js).
export function getSulcusReminder(result){
    if(!result || !result.sulcusAdjusted) return null;
    if(result.recommendedPower !== null && result.bagRecommendedPower !== null){
        let delta = result.recommendedPower - result.bagRecommendedPower;
        let shown = (delta > 0)? "+" + delta.toFixed(1) : (delta < 0)? delta.toFixed(1) : "0.0";
        return "Bag-to-sulcus adjustment: bag " + result.bagRecommendedPower.toFixed(1)
            + " D → selected sulcus " + result.recommendedPower.toFixed(1) + " D (" + shown + " D)."
            + " Experimental 5% adjustment (Cartwright et al); fixation and optic-capture effects are not modelled.";
    }
    return "Sulcus: experimental 5% adjustment (Cartwright et al); fixation and optic-capture effects are not modelled.";
}

// MRSE = Sphere + Cylinder / 2 (SRS 1.1). Computed here because the UI shows it
// live as the user types, before anything is sent. Cylinder sign matters.
//
// This is the one formula duplicated between browser and server, and it is
// duplicated knowingly: a round trip per keystroke would be worse. The server
// recomputes it from sphere and cylinder for the actual calculation ([F5],
// Data!E23) — this copy is display only and never feeds the recommendation.
export function calculateMRSE(sphere, cylinder){
    let s = parseFloat(sphere);
    let c = parseFloat(cylinder);
    if(isNaN(s)) return "";
    if(isNaN(c)) c = 0;
    return (s + c / 2).toFixed(2);
}

// Power labelling (SRS 1.5, Peter 21 Jul):
// if the exchange does not change the IOL platform, label both the current and
// the new power; if the platform changes, label only the new power.
// The "Actual" row is the implanted power after the A-constant adjustment
// (the term "Anchor" is renamed "Actual" in the UI, Dante 21 Jul).
export function getRowLabels(row, changeModel){
    let labels = [];
    if(row.isActual){
        // Same platform: the implanted power is on the same scale as the
        // candidates, so it is labelled as the current power too.
        labels.push(changeModel? "Actual" : "Actual — current power");
    }
    if(row.isRecommended) labels.push("Rec — new power");
    return labels;
}
